import { useMemo } from 'react';
import { useUser } from '@/queries/useUser';
import { useStoreData } from '@/queries/useStoreData';
import { hasActiveSubscription } from '@/features/subscriptions/logic/subscriptionStatus.logic';
import { isPaidProduct } from '@/lib/helpers';
import { GENERIC_STATUS, INTEGRATION_TYPE } from '@/lib/constants';
import { useOnboardingStepStatus } from './useOnboardingStepStatus';

export interface UseDirectDepositSetupBannerOptions {
    /**
     * If true, requires onboarding steps (profile image + social links) to be completed
     * before showing the banner. Defaults to true.
     */
    requireOnboardingSteps?: boolean;
}

/**
 * Hook to determine if the direct deposit setup banner should be shown.
 * Shows when user has:
 * - Active subscription
 * - Paid products
 * - No direct deposit setup (Stripe, PayPal, or Stan Payment)
 * - (Optional) Onboarding steps completed if onboarding dashboard is active
 *
 * @param options - Configuration options for the banner logic
 * @returns Object with `showBanner` boolean indicating if banner should be displayed
 */
export function useDirectDepositSetupBanner(options: UseDirectDepositSetupBannerOptions = {}): { showBanner: boolean } {
    const { requireOnboardingSteps = true } = options;
    const { user } = useUser();
    const { data: storeData } = useStoreData(user?.default_store_id);
    const { isProfileImageUploaded, isSocialLinksAdded } = useOnboardingStepStatus();

    const showOnboardingDashboard = useMemo(() => {
        return user?.['onboarding-dashboard']?.status === GENERIC_STATUS.ACTIVE;
    }, [user]);

    const showBanner = useMemo(() => {
        // Must have active subscription
        if (!hasActiveSubscription(user)) {
            return false;
        }

        // Must have paid products
        if (!storeData || storeData.length === 0) return false;
        const store = storeData[0];
        if (!store || !store.pages) return false;

        const hasPaidProducts = store.pages.some(page => {
            if (!page.data?.product) return false;
            return isPaidProduct(page.data.product);
        });

        if (!hasPaidProducts) return false;

        // Must NOT have direct deposit setup
        const integrations = user?.integrations || {};
        const hasDirectDeposit =
            !!integrations[INTEGRATION_TYPE.STRIPE_CONNECT] ||
            !!integrations[INTEGRATION_TYPE.PAYPAL] ||
            !!integrations[INTEGRATION_TYPE.STAN_PAYMENT];

        if (hasDirectDeposit) return false;

        // If user has onboarding dashboard and we require onboarding steps,
        // only show if past 2nd step (profile image + social links)
        if (requireOnboardingSteps && showOnboardingDashboard) {
            if (!isProfileImageUploaded || !isSocialLinksAdded) {
                return false;
            }
        }

        return true;
    }, [user, storeData, showOnboardingDashboard, isProfileImageUploaded, isSocialLinksAdded, requireOnboardingSteps]);

    return { showBanner };
}
